import React, { FC, ReactNode, useEffect, useRef, useState, useMemo, useCallback } from 'react';

export enum LOAD_PRIORITY {
  CRITICAL = 0, // 首屏关键内容
  HIGH = 1, // 首屏下方内容
  MEDIUM = 2, // 侧边内容
  LOW = 3, // 页脚等非关键内容
}

interface LazyLoaderProps {
  children: ReactNode;
  priority?: LOAD_PRIORITY;
  placeholder?: ReactNode;
  predictSize?: boolean | { width: number; height: number };
  threshold?: number;
}

const LazyLoader: FC<LazyLoaderProps> = ({
  children,
  priority = LOAD_PRIORITY.MEDIUM,
  placeholder,
  predictSize = true,
  threshold = 0.01,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [hasError, setHasError] = useState(false);

  // 动态计算rootMargin
  const rootMargin = useMemo(() => {
    if (typeof predictSize === 'object') {
      return `${Math.min(300, predictSize.height * 0.5)}px 0px`;
    }
    return `${priority * 100}px 0px`;
  }, [priority, predictSize]);

  // 尺寸预测逻辑
  const measureSize = useCallback(() => {
    if (!containerRef.current || !predictSize) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions((prev) => ({
          width: Math.max(prev?.width || 0, width),
          height: Math.max(prev?.height || 0, height),
        }));
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [predictSize]);

  // Intersection Observer逻辑
  useEffect(() => {
    if (!containerRef.current || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin,
        threshold,
      }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [rootMargin, threshold, isVisible]);

  // 初始化尺寸测量
  useEffect(() => {
    if (predictSize) {
      measureSize();
    }
  }, [measureSize, predictSize]);

  // 占位符样式计算
  const placeholderStyle = useMemo(() => {
    const baseStyle = {
      minWidth: '100%',
      background: 'linear-gradient(110deg, #f5f6f7 8%, #eee 18%, #f5f6f7 33%)',
      backgroundSize: '200% 100%',
      animation: '1.5s shine linear infinite',
    };

    if (!dimensions) return baseStyle;

    return {
      ...baseStyle,
      minWidth: dimensions.width || '100%',
      minHeight: dimensions.height || 'auto',
      aspectRatio: dimensions.width && dimensions.height ? `${dimensions.width}/${dimensions.height}` : undefined,
    };
  }, [dimensions]);

  if (hasError) {
    return <div style={{ color: 'gray' }}>Reload...</div>;
  }

  return (
    <div
      ref={containerRef}
      style={!isVisible ? placeholderStyle : undefined}
      role="region"
      aria-live="polite"
      data-lazy-priority={priority}>
      {isVisible ? children : placeholder || <div style={placeholderStyle} aria-hidden="true" />}
    </div>
  );
};

// 错误边界组件
class ErrorBoundary extends React.Component<{ fallback: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// 导出带错误边界的组件
export default Object.assign(
  (props: LazyLoaderProps) => (
    <ErrorBoundary fallback={<div>loading failed</div>}>
      <LazyLoader {...props} />
    </ErrorBoundary>
  ),
  { Priority: LOAD_PRIORITY }
);
